'use client';

import * as Sentry from '@sentry/nextjs';
import { useEffect, useRef } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useProOffer } from '@/features/subscription/hooks/use-pro-offer';
import { useAuthStore } from '@/shared/stores/auth-store';

// Paths exempt from gating - accessible regardless of profile/subscription state.
// /ai: taboo-rag gives signed-in non-subscribers their own free questions and
// answers the 429 with the Subscribe CTA, so the paywall lives there.
const EXEMPT_PATHS = ['/choose-plan', '/redeem', '/account/complete', '/auth/whop-callback', '/ai'];

function isExemptPath(pathname: string): boolean {
  // Course/bundle detail + play routes are exempt — OTP gate handles auth inline,
  // and purchased course access is enforced at the API level, not the route level.
  // Identifier accepts both numeric ids and kebab-case slugs (matches proxy.ts).
  if (/^\/courses\/(bundles\/)?[a-z0-9][a-z0-9-]*/.test(pathname)) return true;
  return EXEMPT_PATHS.some((exempt) => pathname === exempt || pathname.startsWith(`${exempt}/`));
}

/**
 * Enforces the onboarding decision tree via effect-driven redirects:
 * 1. Not subscribed? -> redirect to /choose-plan
 * 2. Profile incomplete? -> redirect to /account/complete
 *
 * Renders null — mount under <Suspense> as a SIBLING of the gated content
 * (never wrapping it) so the usePathname read stays out of the static shell.
 */
export function AccessGateWatcher() {
  const router = useRouter();
  const pathname = usePathname();
  const { isAuthenticated, isInitialized, isProfileComplete, isSubscribed, _hasHydrated, user } =
    useAuthStore();
  const { onOffer: proOnOffer, isPending: proOfferPending } = useProOffer();

  // Redirect loop safeguard: track redirects per pathname
  const redirectCountRef = useRef<{ pathname: string; count: number; timestamp: number }>({
    pathname: '',
    count: 0,
    timestamp: 0,
  });

  useEffect(() => {
    // Wait for auth store to be ready
    if (!isInitialized || !_hasHydrated) return;

    // Only gate authenticated users
    if (!isAuthenticated) return;

    // Redirect completed users away from /account/complete
    if (pathname === '/account/complete' && isProfileComplete) {
      Sentry.addBreadcrumb({
        category: 'navigation',
        message: 'AccessGate: profile already complete, redirecting away from /account/complete',
        level: 'info',
        data: { from: pathname, to: '/' },
      });
      router.replace('/');
      return;
    }

    // Redirect subscribed users away from /choose-plan to prevent double purchase.
    // Exceptions: redeem_code subscribers can upgrade to a paid plan, and a
    // subscriber Pro is on offer to (`useProOffer`) can upgrade to it. While
    // an eligible user's plans load, wait rather than bounce them.
    const subscribedOnChoosePlan = pathname === '/choose-plan' && isSubscribed;
    if (subscribedOnChoosePlan && proOfferPending) return;
    if (subscribedOnChoosePlan && user?.provider !== 'redeem_code' && !proOnOffer) {
      Sentry.addBreadcrumb({
        category: 'navigation',
        message: 'AccessGate: subscribed user on /choose-plan, redirecting to /',
        level: 'info',
        data: { from: pathname, to: '/' },
      });
      router.replace('/');
      return;
    }

    // Never gate exempt paths
    if (isExemptPath(pathname)) return;

    // Redirect loop safeguard
    const now = Date.now();
    const tracker = redirectCountRef.current;
    if (tracker.pathname === pathname && now - tracker.timestamp < 5000) {
      tracker.count++;
      if (tracker.count >= 3) return; // Stop redirecting
    } else {
      redirectCountRef.current = { pathname, count: 0, timestamp: now };
    }

    // 1. Not subscribed -> /choose-plan (checked BEFORE profile)
    if (!isSubscribed) {
      Sentry.addBreadcrumb({
        category: 'navigation',
        message: 'AccessGate: not subscribed, redirecting to /choose-plan',
        level: 'info',
        data: { from: pathname, to: '/choose-plan' },
      });
      redirectCountRef.current.count++;
      router.replace('/choose-plan');
      return;
    }

    // 2. Profile incomplete -> /account/complete
    if (!isProfileComplete) {
      Sentry.addBreadcrumb({
        category: 'navigation',
        message: 'AccessGate: profile incomplete, redirecting to /account/complete',
        level: 'info',
        data: { from: pathname, to: '/account/complete' },
      });
      redirectCountRef.current.count++;
      router.replace('/account/complete');
      return;
    }
  }, [
    isInitialized,
    _hasHydrated,
    isAuthenticated,
    isProfileComplete,
    isSubscribed,
    user,
    pathname,
    router,
    proOnOffer,
    proOfferPending,
  ]);

  return null;
}
